import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from "../../public/eventli-logo.svg";
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEye, FaEyeSlash, FaArrowLeft } from 'react-icons/fa';

const SignupPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formStage, setFormStage] = useState('signup');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    location: '',
    socialMediaLinks: '',
    enableWallet: true,
    autoTopUp: false,
    initialFunds: 0,
    paymentMethod: '',
    referralCode: ''
  });

  const navigate = useNavigate();

  // Slides content
  const slides = [
    {
      title: "Welcome to Eventli",
      contents: [
        "Find & Book Events Easily -- Explore exciting events and secure your tickets in seconds.",
        "Earn Rewards -- Get Eventli Coins for attending, sharing, reviewing, and referring friends. Use them for discounts!",
        "Engage & Connect -- Like, share, follow, and interact with event communities."
      ]
    },
    {
      title: "Welcome to Eventli",
      contents: [
        "Manage Your Wallet & Portfolio -- Load up your Eventli Wallet for quick payments and track your event history.",
        "Exclusive Access -- Get early access to premium events and special offers.",
        "Personalized Recommendations -- Discover events tailored to your interests."
      ]
    },
  ];

  // Auto slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form stage transitions
  const handleSignupSubmit = (e) => {
    e.preventDefault();
    setFormStage('preferences');
  };

  const handlePreferencesSubmit = (e) => {
    e.preventDefault();
    setFormStage('wallet');
  };

  // Go back to previous form stage
  const handleGoBack = () => {
    if (formStage === 'preferences') {
      setFormStage('signup');
    } else if (formStage === 'wallet') {
      setFormStage('preferences');
    }
  };

  // Final signup submission
  const handleFinalSignup = async (e) => {
    e.preventDefault();
  
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
  
    setLoading(true);
  
    try {
      const response = await fetch('https://event-li-backend.onrender.com/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.message || 'Signup failed');
      }
  
      // Save data to localStorage
      localStorage.setItem('userjwt', data.token);
      localStorage.setItem('userdetails', JSON.stringify(data.user));
  
      // Show success message with account details if wallet was created
      if (data.user.virtualAccount) {
        toast.success(
          <div>
            <p>Account created successfully!</p>
            <p>Your virtual account details:</p>
            <p>Bank: {data.user.virtualAccount.bankName}</p>
            <p>Account Number: {data.user.virtualAccount.accountNumber}</p>
            <p>Account Name: {data.user.virtualAccount.accountName}</p>
          </div>,
          {
            position: "top-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else {
        toast.success('Account created successfully!', {
          position: "top-center",
          autoClose: 3000,
        });
      }
  
      setTimeout(() => navigate("/dashboard"), 3000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Navigate to login page
  const goToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      {/* Left Side - Slides */}
      <motion.div 
        className="w-full md:w-1/2 bg-navy-blue p-4 sm:p-6 md:p-8 flex flex-col justify-center relative overflow-hidden"
        initial={{ backgroundColor: "#0a0a3c" }}
        animate={{ backgroundColor: "#0a0a3c" }}
        transition={{ duration: 0.5 }}
      >
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-16 sm:w-24 h-16 sm:h-24 bg-[#FCD20B] rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-20 sm:w-32 h-20 sm:h-32 bg-[#FCD20B] rounded-full translate-x-1/2 translate-y-1/2"></div>
        <div className="absolute top-20 right-0 w-20 sm:w-32 h-20 sm:h-32 border-2 border-[#A19EFF] opacity-30 rounded-lg transform rotate-12"></div>
        <div className="absolute bottom-20 left-0 w-20 sm:w-32 h-20 sm:h-32 border-2 border-[#A19EFF] opacity-30 rounded-lg transform -rotate-12"></div>
        
        <div className="z-10 px-4 sm:px-8 py-8 sm:py-16 h-full flex items-center">
          {slides.map((slide, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 100 }}
              animate={{ 
                opacity: currentSlide === index ? 1 : 0,
                x: currentSlide === index ? 0 : 1000,
                display: currentSlide === index ? 'flex' : 'none'
              }}
              transition={{ duration: 0.10, ease: "easeInOut" }}
              className="text-white flex flex-col justify-center h-full"
            >
              <h2 className="text-xl sm:text-2xl font-semibold text-center mb-6 sm:mb-8">{slide.title}</h2>
              <div className="space-y-3 sm:space-y-4">
                {slide.contents.map((content, i) => (
                  <p key={i} className="text-base sm:text-lg font-thin">{content}</p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Slide indicators */}
        <div className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-1 rounded-full transition-all duration-300 ${
                currentSlide === index ? 'w-6 sm:w-8 bg-white' : 'w-3 sm:w-4 bg-gray-400'
              }`}
            />
          ))}
        </div>
      </motion.div>

      {/* Right Side - Forms */}
      <div className="w-full md:w-1/2 bg-white flex flex-col justify-center overflow-y-auto py-6 px-4 sm:px-6">
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="mb-4 sm:mb-6 text-center flex justify-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-navy-blue">
              <img src={Logo} alt="EventLi Logo" className="h-10 sm:h-12" />
            </h1>
          </div>

          <AnimatePresence mode='wait'>
            {formStage === 'signup' ? (
              <motion.div
                key="signup"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl sm:text-2xl font-medium mb-6 sm:mb-8 text-center">Sign up</h2>
                
                <form className="space-y-4 sm:space-y-5" onSubmit={handleSignupSubmit}>
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block font-medium text-sm sm:text-base mb-1 sm:mb-2">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 h-10 sm:h-[40px] text-sm sm:text-base py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                  
                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block font-medium text-sm sm:text-base mb-1 sm:mb-2">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full h-10 sm:h-[45px] px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  
                  {/* Password Field */}
                  <div>
                    <label htmlFor="password" className="block font-medium text-sm sm:text-base mb-1 sm:mb-2">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full h-10 sm:h-[45px] px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        placeholder="Enter password"
                        required
                        minLength="6"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowPassword((prev) => !prev)}
                        tabIndex={-1}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Confirm Password Field */}
                  <div>
                    <label htmlFor="confirmPassword" className="block font-medium text-sm sm:text-base mb-1 sm:mb-2">Confirm Password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 h-10 sm:h-[45px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        placeholder="Confirm password"
                        required
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        tabIndex={-1}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  
                  <motion.button 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full rounded-[30px] py-2 sm:py-3 px-4 text-base sm:text-lg bg-[#FCD20B] hover:bg-yellow-500 text-black font-medium transition duration-300 ease-in-out mt-4"
                  >
                    Continue
                  </motion.button>

                  <div className="text-center mt-4">
                    <p className="text-sm text-gray-600">
                      Already have an account?{" "}
                      <button 
                        type="button" 
                        onClick={goToLogin} 
                        className="text-blue-600 hover:underline font-medium"
                      >
                        Log in
                      </button>
                    </p>
                  </div>
                </form>
              </motion.div>
            ) : formStage === 'preferences' ? (
              <motion.div
                key="preferences"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-4 sm:space-y-6"
              >
                <div className="flex items-center mb-2">
                  <button 
                    type="button" 
                    onClick={handleGoBack}
                    className="p-2 mr-2 rounded-full hover:bg-gray-100 transition-colors"
                    aria-label="Go back"
                  >
                    <FaArrowLeft className="text-gray-600" />
                  </button>
                  <h2 className="text-xl sm:text-2xl font-medium">Your Preferences</h2>
                </div>
                
                {/* Preferences Form */}
                <form className="space-y-4 sm:space-y-5" onSubmit={handlePreferencesSubmit}>
                  {/* Location Field */}
                  <div>
                    <label htmlFor="location" className="block font-medium text-sm sm:text-base mb-1 sm:mb-2">Location</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 h-10 sm:h-[40px] text-sm sm:text-base py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="Enter your location"
                    />
                  </div>

                  {/* Social Media Links */}
                  <div>
                    <label htmlFor="socialMediaLinks" className="block font-medium text-sm sm:text-base mb-1 sm:mb-2">Social Media Links</label>
                    <input
                      type="text"
                      id="socialMediaLinks"
                      name="socialMediaLinks"
                      value={formData.socialMediaLinks}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 h-10 sm:h-[40px] text-sm sm:text-base py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="Enter links separated by commas"
                    />
                  </div>

                  <motion.button 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full rounded-[30px] py-2 sm:py-3 px-4 text-base sm:text-lg bg-[#FCD20B] hover:bg-yellow-500 text-black font-medium transition duration-300 ease-in-out mt-4"
                  >
                    Continue
                  </motion.button>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="wallet"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-4 sm:space-y-6"
              >
                <div className="flex items-center mb-2">
                  <button 
                    type="button" 
                    onClick={handleGoBack}
                    className="p-2 mr-2 rounded-full hover:bg-gray-100 transition-colors"
                    aria-label="Go back"
                  >
                    <FaArrowLeft className="text-gray-600" />
                  </button>
                  <h2 className="text-xl sm:text-2xl font-medium">Wallet Setup</h2>
                </div>
                
                {/* Wallet Form */}
                <form className="space-y-4 sm:space-y-5" onSubmit={handleFinalSignup}>
                  {/* Wallet Options */}
                  <div>
                    <label className="block text-[#1E1E1E] font-normal text-sm sm:text-base mb-1 sm:mb-2">
                      Enable Wallet?
                    </label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          className="form-radio h-4 w-4 text-[#FCD20B]"
                          checked={formData.enableWallet}
                          onChange={() => setFormData({...formData, enableWallet: true})}
                        />
                        <span className="ml-2 text-sm sm:text-base">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          className="form-radio h-4 w-4 text-[#FCD20B]"
                          checked={!formData.enableWallet}
                          onChange={() => setFormData({...formData, enableWallet: false})}
                        />
                        <span className="ml-2 text-sm sm:text-base">No</span>
                      </label>
                    </div>
                  </div>

                  {/* Auto Top Up */}
                  <div>
                    <label className="block text-[#1E1E1E] font-normal text-sm sm:text-base mb-1 sm:mb-2">
                      Set Auto-Top Up?
                    </label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          className="form-radio h-4 w-4 text-[#FCD20B]"
                          checked={formData.autoTopUp}
                          onChange={() => setFormData({...formData, autoTopUp: true})}
                        />
                        <span className="ml-2 text-sm sm:text-base">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          className="form-radio h-4 w-4 text-[#FCD20B]"
                          checked={!formData.autoTopUp}
                          onChange={() => setFormData({...formData, autoTopUp: false})}
                        />
                        <span className="ml-2 text-sm sm:text-base">No</span>
                      </label>
                    </div>
                  </div>

                  {/* Initial Funds */}
                  <div>
                    <label htmlFor="initialFunds" className="block text-[#1E1E1E] font-normal text-sm sm:text-base mb-1 sm:mb-2">
                      Load Initial Funds (Optional)
                    </label>
                    <input
                      type="number"
                      id="initialFunds"
                      name="initialFunds"
                      value={formData.initialFunds}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 h-10 sm:h-[40px] text-sm sm:text-base py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="Enter amount"
                    />
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label htmlFor="paymentMethod" className="block text-[#1E1E1E] font-normal text-sm sm:text-base mb-1 sm:mb-2">
                      Preferred Payment Method
                    </label>
                    <select
                      id="paymentMethod"
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 h-10 sm:h-[40px] text-sm sm:text-base py-1 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    >
                      <option value="">Select payment method</option>
                      <option value="credit">Credit Card</option>
                      <option value="debit">Debit Card</option>
                      <option value="bank">Bank Transfer</option>
                      <option value="paypal">PayPal</option>
                    </select>
                  </div>

                  {/* Referral Code */}
                  <div>
                    <label htmlFor="referralCode" className="block font-medium text-sm sm:text-base mb-1 sm:mb-2">
                      Referral Code (If Any)
                    </label>
                    <input
                      type="text"
                      id="referralCode"
                      name="referralCode"
                      value={formData.referralCode}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 h-10 sm:h-[40px] text-sm sm:text-base py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      placeholder="Enter referral code"
                    />
                  </div>

                  <motion.button 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="w-full rounded-[30px] py-2 sm:py-3 px-4 text-base sm:text-lg bg-[#FCD20B] hover:bg-yellow-500 text-black font-medium transition duration-300 ease-in-out flex items-center justify-center mt-4"
                    disabled={loading}
                  >
                    {loading ? (
                      <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 mr-2 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                      </svg>
                    ) : null}
                    {loading ? "Signing Up..." : "Complete Sign Up"}
                  </motion.button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {/* ToastContainer for toast messages */}
        <ToastContainer />
      </div>
    </div>
  );
};

export default SignupPage;